import { useEffect, useRef } from "react";

interface Leaf {
  id: number;
  x: number;
  y: number;
  size: number;
  rotation: number;
  rotationSpeed: number;
  rotationAccel: number;
  speedX: number;
  speedY: number;
  swayPhase: number;
  swaySpeed: number;
  swayAmplitude: number;
  opacity: number;
  shape: number;
  color: string;
}

const LEAF_COLORS = [
  "hsl(160, 40%, 55%)",
  "hsl(160, 30%, 65%)",
  "hsl(140, 35%, 58%)",
  "hsl(100, 25%, 62%)",
  "hsl(90, 30%, 50%)",
  "hsl(120, 25%, 55%)",
  "hsl(160, 20%, 70%)",
  "hsl(80, 20%, 58%)",
];

function createLeaf(id: number, canvasWidth: number, canvasHeight: number): Leaf {
  return {
    id,
    x: Math.random() * canvasWidth,
    y: Math.random() * canvasHeight - canvasHeight,
    size: 10 + Math.random() * 16,
    rotation: Math.random() * 360,
    rotationSpeed: (Math.random() - 0.5) * 0.3,
    rotationAccel: (Math.random() - 0.5) * 0.005,
    speedX: (Math.random() - 0.5) * 0.3,
    speedY: 0.15 + Math.random() * 0.45,
    swayPhase: Math.random() * Math.PI * 2,
    swaySpeed: 0.008 + Math.random() * 0.015,
    swayAmplitude: 0.3 + Math.random() * 0.6,
    opacity: 0.3 + Math.random() * 0.4,
    shape: Math.floor(Math.random() * 5),
    color: LEAF_COLORS[Math.floor(Math.random() * LEAF_COLORS.length)],
  };
}

function drawLeaf(ctx: CanvasRenderingContext2D, leaf: Leaf) {
  ctx.save();
  ctx.translate(leaf.x, leaf.y);
  ctx.rotate((leaf.rotation * Math.PI) / 180);
  ctx.globalAlpha = leaf.opacity;
  ctx.fillStyle = leaf.color;
  ctx.beginPath();

  const s = leaf.size;
  if (leaf.shape === 0) {
    // Oval leaf
    ctx.ellipse(0, 0, s * 0.4, s, 0, 0, Math.PI * 2);
  } else if (leaf.shape === 1) {
    // Pointed leaf
    ctx.moveTo(0, -s);
    ctx.bezierCurveTo(s * 0.6, -s * 0.3, s * 0.6, s * 0.3, 0, s * 0.2);
    ctx.bezierCurveTo(-s * 0.6, s * 0.3, -s * 0.6, -s * 0.3, 0, -s);
  } else if (leaf.shape === 2) {
    // Round petal
    ctx.arc(0, 0, s * 0.45, 0, Math.PI * 2);
  } else if (leaf.shape === 3) {
    // Tropical wide leaf
    ctx.moveTo(0, -s);
    ctx.bezierCurveTo(s * 0.8, -s * 0.4, s * 0.7, s * 0.5, 0, s * 0.3);
    ctx.bezierCurveTo(-s * 0.7, s * 0.5, -s * 0.8, -s * 0.4, 0, -s);
  } else {
    // Thin willow leaf
    ctx.moveTo(0, -s * 1.1);
    ctx.bezierCurveTo(s * 0.3, -s * 0.4, s * 0.3, s * 0.4, 0, s * 0.3);
    ctx.bezierCurveTo(-s * 0.3, s * 0.4, -s * 0.3, -s * 0.4, 0, -s * 1.1);
  }

  ctx.fill();

  // Midvein
  ctx.strokeStyle = leaf.color;
  ctx.globalAlpha = leaf.opacity * 0.4;
  ctx.lineWidth = 0.6;
  ctx.beginPath();
  ctx.moveTo(0, -s * 0.8);
  ctx.lineTo(0, s * 0.2);
  ctx.stroke();

  ctx.restore();
}

interface FloatingLeavesProps {
  count?: number;
  className?: string;
}

export default function FloatingLeaves({ count = 18, className = "" }: FloatingLeavesProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const leavesRef = useRef<Leaf[]>([]);
  const mouseRef = useRef({ x: -9999, y: -9999 });
  const animRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Performance gates: skip the canvas animation entirely when the user
    // prefers reduced motion or when running on small/low-power devices.
    // The decorative leaves are non-essential — saving the rAF loop here
    // dramatically reduces main-thread work and battery use on mobile.
    const prefersReducedMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const isSmallViewport = window.innerWidth < 768;
    if (prefersReducedMotion || isSmallViewport) {
      return;
    }

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    leavesRef.current = Array.from({ length: count }, (_, i) =>
      createLeaf(i, canvas.width, canvas.height * 2)
    );

    const handleMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };
    window.addEventListener("mousemove", handleMouseMove);

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const mouse = mouseRef.current;

      leavesRef.current.forEach((leaf) => {
        // Mouse repulsion
        const dx = leaf.x - mouse.x;
        const dy = leaf.y - mouse.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 90) {
          const force = (90 - dist) / 90;
          leaf.x += (dx / dist) * force * 1.5;
          leaf.y += (dy / dist) * force * 1;
          leaf.rotationSpeed += force * 0.8;
        }

        // Organic sway
        leaf.swayPhase += leaf.swaySpeed;
        const sway = Math.sin(leaf.swayPhase) * leaf.swayAmplitude;

        leaf.x += leaf.speedX + sway;
        leaf.y += leaf.speedY;
        leaf.rotationSpeed += leaf.rotationAccel;
        leaf.rotationSpeed *= 0.995; // damping
        leaf.rotation += leaf.rotationSpeed;

        // Occasional direction shift
        if (Math.random() < 0.002) {
          leaf.speedX += (Math.random() - 0.5) * 0.2;
        }

        // Reset when off screen
        if (leaf.y > canvas.height + leaf.size * 2) {
          leaf.y = -leaf.size * 2;
          leaf.x = Math.random() * canvas.width;
          leaf.speedX = (Math.random() - 0.5) * 0.3;
        }
        if (leaf.x < -leaf.size * 2) leaf.x = canvas.width + leaf.size;
        if (leaf.x > canvas.width + leaf.size * 2) leaf.x = -leaf.size;

        drawLeaf(ctx, leaf);
      });

      animRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, [count]);

  return (
    <canvas
      ref={canvasRef}
      className={`absolute inset-0 w-full h-full pointer-events-none ${className}`}
    />
  );
}
